import React from 'react';
import { Link } from 'react-router-dom';
import { Course, Instructor } from '../types';
import { useTracks } from '../hooks/useTracks';
import { findTrackByName } from '../data/defaultTracks';

interface CourseCardProps {
  course: Course;
  instructor?: Instructor;
}

const getInstructorInitials = (name: string) =>
  name
    .split(' ')
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const CourseCard: React.FC<CourseCardProps> = ({ course, instructor }) => {
  const tracks = useTracks();
  const trackInfo = findTrackByName(tracks, course.track);

  return (
    <article className="course-card">
      <div className="course-card-header">
        <div className="course-card-track-mark" style={{ borderTopColor: trackInfo.color }} aria-hidden="true">
          <span className="track-icon">{trackInfo.icon}</span>
        </div>
        <span className="course-skill-level">{course.skillLevel}</span>
      </div>

      <div className="course-card-body">
        <h3 className="course-card-title">{course.title}</h3>
        <p className="course-card-description">{course.shortDescription}</p>

        <div className="course-card-highlights">
          <div className="course-highlight-pill">
            <span className="course-highlight-label">Format</span>
            <span className="course-highlight-value">{course.delivery}</span>
          </div>
          <div className="course-highlight-pill">
            <span className="course-highlight-label">Duration</span>
            <span className="course-highlight-value">{course.duration}</span>
          </div>
        </div>

        {instructor && (
          <div className="course-card-instructor">
            {instructor.photoUrl ? (
              <img
                className="course-card-instructor-photo"
                src={instructor.photoUrl}
                alt={instructor.name}
              />
            ) : (
              <div className="course-card-instructor-avatar" aria-hidden="true">
                {getInstructorInitials(instructor.name)}
              </div>
            )}
            <div className="course-card-instructor-copy">
              <span className="course-card-instructor-label">Instructor</span>
              <span className="course-card-instructor-name">{instructor.name}</span>
              {instructor.title && (
                <span className="course-card-instructor-title">{instructor.title}</span>
              )}
            </div>
          </div>
        )}

        {course.certificationAlignment.length > 0 && (
          <div className="course-card-certs">
            {course.certificationAlignment.map((cert, index) => (
              <span key={index} className="cert-tag">
                {cert}
              </span>
            ))}
          </div>
        )}
      </div>

      <Link to={`/courses/${course.id}`} className="course-card-link">
        Learn More
      </Link>
    </article>
  );
};

export default CourseCard;
